import { useCallback, useEffect, useRef } from 'react';
import { MESSAGE_EVENT, MESSAGE_TYPES } from '../constants/simulationConstants';
import { postMessage } from '../helpers/utils';
import { useSimulationContext } from '../providers/SimulationProvider';

const LIVE_CHAT_EMOTION_TIMEOUT_MS = 500;

/**
 * Returns a fresh user emotion for a Live Chat turn.
 *
 * Mirrors {@link useVoiceActivityDetection}: the parent is asked for the latest
 * camera reading via USER_EMOTION_LIVE_CHAT right before the clip ships, because
 * the interval poll can be minutes stale by the time the user finishes speaking.
 */
function useRequestLiveChatUserEmotion() {
  const { userEmotion, isEmotionRecognitionEnabled } = useSimulationContext();
  const userEmotionRef = useRef(null);
  const pendingRef = useRef(null);

  useEffect(() => {
    userEmotionRef.current = isEmotionRecognitionEnabled ? userEmotion : null;

    if (pendingRef.current) {
      pendingRef.current.resolve(userEmotionRef.current);
      pendingRef.current = null;
    }
  }, [userEmotion, isEmotionRecognitionEnabled]);

  return useCallback(() => {
    if (!isEmotionRecognitionEnabled) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const timeoutId = setTimeout(() => {
        pendingRef.current = null;
        resolve(userEmotionRef.current);
      }, LIVE_CHAT_EMOTION_TIMEOUT_MS);

      pendingRef.current = {
        resolve: (value) => {
          clearTimeout(timeoutId);
          resolve(value ?? null);
        }
      };

      postMessage({
        type: MESSAGE_TYPES.REQUEST_USER_EMOTION,
        payload: { event: MESSAGE_EVENT.USER_EMOTION_LIVE_CHAT }
      });
    });
  }, [isEmotionRecognitionEnabled]);
}

export default useRequestLiveChatUserEmotion;
